import { zodResolver } from "@hookform/resolvers/zod";
import { DynamicForm } from "@shared/components/DynamicForm";
import { useToast } from "@shared/hooks/use-toast";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { FormField } from "@shared/services/FormServices";
import { Button } from "@shared/ui/button";
import { Card, CardContent } from "@shared/ui/card";
import { Tooltip, TooltipContent, TooltipTrigger } from "@shared/ui/tooltip";
import {
  ChevronRight,
  HelpCircle,
  Link,
  ListOrdered,
  Pencil,
  PlusCircle,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { useUpdateSopDocuments } from "../../../hooks";

// Schema for help resources form
const helpResourcesSchema = z
  .object({
    resourceType: z.enum(["instruction", "video", "file"], {
      required_error: "Please select a resource type",
    }),
    resourceName: z.string().min(1, "Resource name is required"),
    resourceContent: z.string().optional(),
    resourceFile: z.any().optional(),
  })
  .refine(
    (data) =>
      (data.resourceType === "file" && data.resourceFile) ||
      (data.resourceType !== "file" && data.resourceContent),
    {
      message: "Either a file or content is required based on the type",
      path: ["resourceContent"], // points to the right field
    }
  );

type ResourceType = "instruction" | "video" | "file";

interface ResourceItem {
  id: number;
  type: ResourceType;
  name: string;
  content: string;
  file?: File;
  apiType: number; // 0=instruction, 1=video, 2=file
  existingId?: number; // For existing API documents
}

interface SopHelpStepProps {
  state: any;
  onUpdateState: (updates: any) => void;
  onNext?: () => void;
  onPrevious?: () => void;
  readOnly?: boolean;
  templateId: string;
  onStepSave?: (stepIndex: number, data: any, isDirty: boolean) => void;
}

const SopHelpStep: React.FC<SopHelpStepProps> = ({
  state,
  onUpdateState,
  onNext,
  onPrevious,
  readOnly = false,
  templateId,
  onStepSave,
}) => {
  // ✅ Get SOP ID from state like other components
  const sopId = state.basicInfo?.sopId || templateId;

  const [resources, setResources] = useState<ResourceItem[]>([]);
  const [showAddResource, setShowAddResource] = useState(false);
  const [editingResourceId, setEditingResourceId] = useState<number | null>(
    null
  );
  const [resourcesLoaded, setResourcesLoaded] = useState(false);

  const resourceForm = useForm<z.infer<typeof helpResourcesSchema>>({
    resolver: zodResolver(helpResourcesSchema),
    defaultValues: {
      resourceType: "instruction",
      resourceName: "",
      resourceContent: "",
    },
  });

  const watchedResourceType = resourceForm.watch("resourceType");
  const utilityId = useRemoteUtilityId();
  const { toast } = useToast();

  // Hook for updating SOP documents via API
  const updateSopDocuments = useUpdateSopDocuments();

  // Display help documents in edit mode
  const isEditMode = Boolean(state.basicInfo?.sopId);
  const helpDocuments = state.helpDocuments || [];

  // Map resource type to API document type
  const getApiType = (resourceType: ResourceType): number => {
    switch (resourceType) {
      case "instruction":
        return 0;
      case "video":
        return 1;
      case "file":
        return 2;
      default:
        return 0;
    }
  };

  // Map API type back to resource type
  const getResourceTypeFromApi = (apiType: number): ResourceType => {
    switch (apiType) {
      case 0:
        return "instruction";
      case 1:
        return "video";
      case 2:
        return "file";
      default:
        return "file";
    }
  };

  // ✅ Simplified effect to handle resources properly
  useEffect(() => {
    console.log("🔄 Help component effect:", {
      sopId,
      isEditMode,
      stateResources: state.resources?.length || 0,
      resourcesLoaded,
      currentResourcesLength: resources.length,
    });

    // If we already loaded resources, don't reload
    if (resourcesLoaded && resources.length > 0) {
      return;
    }

    // Handle state resources (from navigation back, edit mode, or create mode)
    if (state.resources && state.resources.length > 0) {
      const formattedResources = state.resources.map(
        (resource: any, index: number) => ({
          id: resource.id || Date.now() + index,
          type: resource.type || getResourceTypeFromApi(resource.apiType || 2),
          name: resource.name || `Resource ${index + 1}`,
          content:
            resource.content || resource.url || resource.description || "",
          apiType:
            resource.apiType !== undefined
              ? resource.apiType
              : getApiType(resource.type || "file"),
          existingId: resource.existingId || resource.id,
          file: resource.file,
        })
      );

      setResources(formattedResources);
      setResourcesLoaded(true);

      return;
    }

    // Handle empty state - initialize empty resources
    if (!resourcesLoaded) {
      setResources([]);
      setResourcesLoaded(true);

      // Initialize state if not exists
      if (!state.resources) {
        onUpdateState({
          resources: [],
        });
      }
    }
  }, [
    sopId,
    isEditMode,
    state.resources,
    resourcesLoaded,
    onUpdateState,
    resources.length,
  ]);

  // ✅ Force refresh resources when state changes (for navigation scenarios)
  useEffect(() => {
    if (
      state.resources &&
      state.resources.length > 0 &&
      resources.length === 0 &&
      resourcesLoaded
    ) {
      const formattedResources = state.resources.map(
        (resource: any, index: number) => ({
          id: resource.id || Date.now() + index,
          type: resource.type || getResourceTypeFromApi(resource.apiType || 2),
          name: resource.name || `Resource ${index + 1}`,
          content:
            resource.content || resource.url || resource.description || "",
          apiType:
            resource.apiType !== undefined
              ? resource.apiType
              : getApiType(resource.type || "file"),
          existingId: resource.existingId || resource.id,
          file: resource.file,
        })
      );

      setResources(formattedResources);
    }
  }, [state.resources, resources.length, resourcesLoaded]);

  // ✅ Additional effect to handle resources population for edit mode/navigation
  useEffect(() => {
    // Force populate resources if coming back from other steps or in edit mode
    if (
      state.resources &&
      state.resources.length > 0 &&
      resources.length === 0
    ) {
      const formattedResources = state.resources.map(
        (resource: any, index: number) => ({
          id: resource.id || Date.now() + index,
          type: resource.type || getResourceTypeFromApi(resource.apiType || 2),
          name: resource.name || `Resource ${index + 1}`,
          content:
            resource.content || resource.url || resource.description || "",
          apiType:
            resource.apiType !== undefined
              ? resource.apiType
              : getApiType(resource.type || "file"),
          existingId: resource.existingId || resource.id,
          file: resource.file,
        })
      );

      setResources(formattedResources);
    }
  }, [state.resources, resources.length]);

  // ✅ Debug effect to track state changes
  useEffect(() => {
    console.log("📊 Resources state updated:", {
      resourcesCount: resources.length,
      stateResourcesCount: state.resources?.length || 0,
      sopId,
      isEditMode,
      resourcesLoaded,
    });
  }, [resources, state.resources, sopId, isEditMode, resourcesLoaded]);

  // Define form fields for adding/editing resources
  const resourceFormFields: FormField[] = useMemo(() => {
    // Get the current editing resource if any
    const editingResource = editingResourceId
      ? resources.find((r) => r.id === editingResourceId)
      : null;

    return [
      {
        name: "resourceType",
        label: "Resource Type",
        type: "select" as const,
        required: true,
        options: [
          { value: "instruction", label: "📋 Instructions" },
          { value: "video", label: "🎥 Video/Link" },
          { value: "file", label: "📁 File Upload" },
        ],
        helperText: "Choose the type of help resource you want to add",
        showHelperTooltip: true,
        tooltipText:
          "Instructions are for step-by-step text guidance, Video/Link for external resources, and File Upload for documents.",
        disabled: readOnly,
        className: "col-span-6",
        classes: {
          container: "w-full mb-4",
          label: "block text-sm font-medium text-gray-700 mb-2",
          select: `w-full h-10 px-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors cursor-pointer ${
            readOnly ? "bg-gray-50 cursor-not-allowed" : "bg-white"
          }`,
          error: "text-red-600 text-sm mt-1",
          helperText: "text-xs text-gray-500 mt-1",
        },
        fullWidth: true,
      },
      {
        name: "resourceName",
        label: "Resource Name",
        type: "text" as const,
        required: true,
        placeholder: "Enter a descriptive name for this resource",
        helperText: "Provide a clear, descriptive name for the resource",
        showHelperTooltip: true,
        tooltipText:
          "Give your resource a clear name that helps technicians understand what it contains.",
        disabled: readOnly,
        className: "col-span-6",
        classes: {
          container: "w-full mb-4",
          label: "block text-sm font-medium text-gray-700 mb-2",
          input:
            "w-full h-10 px-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors bg-white",
          error: "text-red-600 text-sm mt-1",
          helperText: "text-xs text-gray-500 mt-1",
        },
        fullWidth: true,
      },
      // Conditional field based on resource type
      ...(watchedResourceType === "file"
        ? [
            {
              name: "resourceFile",
              label: "Upload File",
              type: "file" as const,
              required: editingResourceId ? false : true, // ✅ Make optional when editing
              accept: ".pdf,.doc,.docx,.jpg,.jpeg,.png,.mp4,.mov",
              helperText: editingResource?.file
                ? `Current file: ${
                    editingResource.file.name || "Existing file"
                  } - Select a new file to replace it`
                : "Select a file to upload (PDF, DOC, Images, Videos)",
              showHelperTooltip: true,
              tooltipText:
                "Upload a file that will help technicians complete this SOP. Supported formats: PDF, DOC, Images, Videos.",
              disabled: readOnly,
              className: "col-span-6",
              classes: {
                container: "w-full mb-4",
                label: "block text-sm font-medium text-gray-700 mb-2",
                input:
                  "w-full h-10 px-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors bg-white file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100",
                error: "text-red-600 text-sm mt-1",
                helperText: "text-xs text-gray-500 mt-1",
              },
              fullWidth: true,
            },
          ]
        : [
            {
              name: "resourceContent",
              label:
                watchedResourceType === "video"
                  ? "Video/Link URL"
                  : "Instructions",
              type:
                watchedResourceType === "instruction"
                  ? ("textarea" as const)
                  : ("text" as const),
              required: true,
              placeholder:
                watchedResourceType === "video"
                  ? "Enter video URL or external link"
                  : "Enter detailed instructions here...",
              rows: watchedResourceType === "instruction" ? 4 : undefined,
              helperText:
                watchedResourceType === "video"
                  ? "Provide a direct link to the video or external resource"
                  : "Write step-by-step instructions or guidance",
              showHelperTooltip: true,
              tooltipText:
                watchedResourceType === "video"
                  ? "Enter a direct link to a video or external resource that demonstrates the procedure."
                  : "Write clear, step-by-step instructions that provide additional guidance for completing this SOP.",
              disabled: readOnly,
              className: "col-span-6",
              classes: {
                container: "w-full mb-4",
                label: "block text-sm font-medium text-gray-700 mb-2",
                input:
                  "w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors bg-white resize-vertical",
                error: "text-red-600 text-sm mt-1",
                helperText: "text-xs text-gray-500 mt-1",
              },
              fullWidth: true,
            },
          ]),
    ];
  }, [watchedResourceType, readOnly, editingResourceId, resources]);

  // ✅ API call function - used only on submit
  const saveResourcesViaApi = async (updatedResources: ResourceItem[]) => {
    if (!sopId || !utilityId) {
      return;
    }

    try {
      console.log("🔄 Starting API call with:", {
        sopId,
        utilityId,
        resourcesCount: updatedResources.length,
      });

      // Convert resources to API format
      const apiDocuments = updatedResources.map((resource) => ({
        type: resource.apiType,
        ...(resource.apiType === 0 && {
          description: resource.content,
          ...(resource.existingId && { id: resource.existingId }),
        }),
        ...(resource.apiType === 1 && {
          link: resource.content,
          ...(resource.existingId && { id: resource.existingId }),
        }),
        ...(resource.apiType === 2 && {
          ...(resource.existingId && { id: resource.existingId }),
        }),
      }));

      // Find file resource for upload
      const fileResource = updatedResources.find(
        (r) => r.type === "file" && r.file
      );

      const requestData = {
        helpResourcesUpdated: true,
        remoteUtilityId: utilityId,
        documents: JSON.stringify(apiDocuments),
        ...(fileResource?.file && { file: fileResource.file }),
      };

      await updateSopDocuments.mutateAsync({
        sopId: parseInt(sopId),
        data: requestData,
      });

      toast({
        title: "Success",
        description: "SOP help resources updated successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: `Failed to update SOP help resources: ${
          error?.message || "Unknown error"
        }`,
        variant: "destructive",
      });
      throw error; // Re-throw to prevent navigation
    }
  };

  // ✅ Add resource - LOCAL ONLY (no API call)
  const handleAddResource = useCallback(
    async (data: z.infer<typeof helpResourcesSchema>) => {
      let updatedResources;

      if (editingResourceId !== null) {
        // Edit existing resource
        updatedResources = resources.map((resource) =>
          resource.id === editingResourceId
            ? {
                ...resource,
                type: data.resourceType,
                name: data.resourceName,
                content: data.resourceContent || "",
                file: data.resourceFile,
                apiType: getApiType(data.resourceType),
              }
            : resource
        );
        setEditingResourceId(null);
      } else {
        // Add new resource
        const newResource: ResourceItem = {
          id: Date.now(),
          type: data.resourceType,
          name: data.resourceName,
          content: data.resourceContent || "",
          file: data.resourceFile,
          apiType: getApiType(data.resourceType),
        };
        updatedResources = [...resources, newResource];
      }

      setResources(updatedResources);

      // ✅ Update local state - NO API call
      onUpdateState({
        resources: updatedResources,
      });

      // Reset form
      resourceForm.reset({
        resourceType: "instruction",
        resourceName: "",
        resourceContent: "",
      });
      setShowAddResource(false);

      toast({
        title: "Resource Added",
        description:
          "Resource saved locally. Will be saved when you click Next.",
      });
    },
    [
      resources,
      editingResourceId,
      onUpdateState,
      resourceForm,
      toast,
      onStepSave,
    ]
  );

  // ✅ Delete resource - LOCAL ONLY (no API call)
  const handleDeleteResource = useCallback(
    async (id: number) => {
      const updatedResources = resources.filter(
        (resource) => resource.id !== id
      );

      setResources(updatedResources);

      // ✅ Update local state - NO API call
      onUpdateState({
        resources: updatedResources,
      });

      // ✅ Better toast message that matches the visual behavior
      toast({
        title: "Resource Removed",
        description: "Changes will be saved when you click Next.",
      });
    },
    [resources, onUpdateState, toast]
  );

  const handleEditResource = (resource: ResourceItem) => {
    resourceForm.setValue("resourceType", resource.type);
    resourceForm.setValue("resourceName", resource.name);
    resourceForm.setValue("resourceContent", resource.content);
    if (resource.file) {
      resourceForm.setValue("resourceFile", resource.file);
    }
    setEditingResourceId(resource.id);
    setShowAddResource(true);
  };

  const handleCancelAdd = () => {
    resourceForm.reset({
      resourceType: "instruction",
      resourceName: "",
      resourceContent: "",
    });
    setEditingResourceId(null);
    setShowAddResource(false);
  };

  const getResourceIcon = (type: ResourceType) => {
    switch (type) {
      case "video":
        return <Link className="h-4 w-4" />;
      case "file":
        return <Upload className="h-4 w-4" />;
      case "instruction":
        return <ListOrdered className="h-4 w-4" />;
      default:
        return null;
    }
  };
  useEffect(() => {
    if (watchedResourceType === "file") {
      resourceForm.register("resourceFile", {
        required: editingResourceId ? false : "File is required",
      });
      resourceForm.unregister("resourceContent");
    } else {
      resourceForm.register("resourceContent", {
        required: "Content is required",
      });
      resourceForm.unregister("resourceFile");
    }
  }, [watchedResourceType, resourceForm, editingResourceId]);

  const handleFieldChange = useCallback((fieldName: string, value: any) => {
    // Handle any additional field change logic if needed
  }, []);

  const renderCustomFooter = useCallback(() => {
    return (
      <div className="flex justify-end">
        <Button type="submit" disabled={updateSopDocuments.isPending}>
          {updateSopDocuments.isPending
            ? "Saving..."
            : editingResourceId !== null
            ? "Update Resource"
            : "Add Resource"}
        </Button>
      </div>
    );
  }, [editingResourceId, updateSopDocuments.isPending]);

  // ✅ Submit/Next button - CALLS API THEN NAVIGATES
  const handleSubmit = async () => {
    try {
      // Check conditions that determine API call
      const shouldCallApi = isEditMode && sopId;

      console.log("🔄 Handle submit:", {
        isEditMode,
        sopId,
        shouldCallApi,
        resourcesCount: resources.length,
      });

      // ✅ Call API if in edit mode (existing SOP)
      if (shouldCallApi) {
        await saveResourcesViaApi(resources);
        if (onStepSave) {
          await onStepSave(
            5,
            {
              // step index 5 for help
              resources: resources,
            },
            true
          );
        }
      }

      // ✅ Navigate to next step after API call (or immediately for create mode)
      if (onNext) {
        onNext();
      }
    } catch (error) {
      // Don't navigate if API call failed
    }
  };

  // Map helpDocuments to resource card format for display
  const helpDocumentResources = (helpDocuments || []).map((doc: any) => ({
    id: doc.id,
    type: doc.type === 0 ? "instruction" : doc.type === 1 ? "video" : "file",
    name: doc.name || doc.typeDisplay || "Document",
    content: doc.description || doc.link || "",
    apiType: doc.type,
    existingId: doc.id,
  }));

  // Merge API and local resources for unified management
  const allResources = useMemo(() => {
    // Avoid duplicates: prefer local resources if same id exists
    const localIds = new Set(resources.map((r) => r.id));
    const merged = [
      ...resources,
      ...helpDocumentResources.filter((apiRes) => !localIds.has(apiRes.id)),
    ];
    return merged;
  }, [resources, helpDocumentResources]);

  return (
    <div className="space-y-6">
      {/* Action Buttons */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 mb-4">
          <h3 className="text-lg font-medium">Resource Management</h3>
          <Tooltip>
            <TooltipTrigger asChild>
              <HelpCircle className="w-4 h-4 text-gray-400 hover:text-gray-600 cursor-help" />
            </TooltipTrigger>
            <TooltipContent side="right">
              Add instructions, videos/links, or files to help technicians
              complete this SOP effectively
            </TooltipContent>
          </Tooltip>
        </div>
        <div className="flex gap-2">
          {!readOnly && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                if (showAddResource) {
                  handleCancelAdd();
                } else {
                  resourceForm.reset({
                    resourceType: "instruction",
                    resourceName: "",
                    resourceContent: "",
                  });
                  setEditingResourceId(null);
                  setShowAddResource(true);
                }
              }}
            >
              {showAddResource ? (
                <>
                  <X className="mr-1 h-4 w-4" /> Cancel
                </>
              ) : (
                <>
                  <PlusCircle className="mr-1 h-4 w-4" /> Add Resource
                </>
              )}
            </Button>
          )}
        </div>
      </div>

      {/* Show warning if no sopId */}
      {!sopId && (
        <div className="p-3 bg-amber-50 border border-amber-200 rounded-md">
          <p className="text-sm text-amber-800">
            <strong>Warning:</strong> No SOP ID found in state.basicInfo. Please
            complete the Basic Information step first.
          </p>
        </div>
      )}

      {/* Add/Edit Resource Form */}
      {showAddResource && !readOnly && (
        <Card className="border-dashed border-2 bg-muted/50">
          <CardContent className="pt-6">
            <DynamicForm
              fields={resourceFormFields}
              form={resourceForm}
              onSubmit={handleAddResource}
              onFieldChange={handleFieldChange}
              config={{
                showSubmitButton: false,
                showResetButton: false,
                classes: {
                  form: "space-y-6",
                  button: "px-4 py-2 rounded-md font-medium transition-colors",
                  buttonContainer:
                    "flex justify-end gap-3 pt-6 border-gray-200 mt-8",
                  input:
                    "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
                  textarea:
                    "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-vertical",
                  select:
                    "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white",
                  label: "block text-sm font-medium text-gray-700 mb-1",
                  error: "text-sm text-red-600 mt-1",
                  container: "space-y-1",
                },
              }}
              gridColumns={6}
              responsiveColumns={{ sm: 1, md: 1 }}
              renderCustomFooter={renderCustomFooter}
              className="space-y-4"
            />
          </CardContent>
        </Card>
      )}

      {/* Resources List (unified, editable/deletable) */}
      {allResources.length > 0 ? (
        <div className="space-y-4">
          <h4 className="text-md font-medium text-gray-700">
            Resources ({allResources.length})
          </h4>
          {allResources.map((resource) => (
            <Card key={resource.id}>
              <CardContent className="p-4 flex items-start justify-between">
                <div className="flex items-start gap-3">
                  <div className="bg-muted w-10 h-10 rounded-full flex items-center justify-center shrink-0">
                    {getResourceIcon(resource.type)}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-medium">{resource.name}</p>
                      <span className="text-xs bg-muted px-2 py-0.5 rounded capitalize text-muted-foreground">
                        {resource.type}
                      </span>
                      {resource.file && typeof resource.file === "string" && (
                        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                          File
                        </span>
                      )}
                      {resource.existingId && (
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">
                          Existing
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground line-clamp-2">
                      {resource.content}
                    </p>
                  </div>
                </div>
                {!readOnly && (
                  <div className="flex gap-2 shrink-0">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleEditResource(resource)}
                      type="button"
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDeleteResource(resource.id)}
                      type="button"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-12 border rounded-md border-dashed bg-muted/50">
          <HelpCircle className="h-12 w-12 text-muted-foreground" />
          <h3 className="mt-4 text-lg font-medium">No resources added</h3>
          <p className="mt-2 text-sm text-muted-foreground max-w-xs text-center">
            Add instructions, videos/links, or files to help technicians
            complete this SOP more effectively.
          </p>
          {!readOnly && (
            <Button
              variant="outline"
              className="mt-4"
              onClick={() => setShowAddResource(true)}
              type="button"
            >
              <PlusCircle className="mr-1 h-4 w-4" />
              Add Your First Resource
            </Button>
          )}
        </div>
      )}

      {/* Navigation with API call on submit */}
      {!readOnly && (
        <div className="flex justify-between space-x-2 pt-6 border-t border-gray-200">
          <Button
            variant="outline"
            onClick={onPrevious}
            type="button"
            className="flex items-center gap-2 px-6 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50"
          >
            Back
          </Button>
          <Button
            onClick={handleSubmit}
            type="button"
            disabled={updateSopDocuments.isPending}
            className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {updateSopDocuments.isPending ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Saving...
              </>
            ) : (
              <>
                Next
                <ChevronRight className="h-4 w-4" />
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  );
};

export default SopHelpStep;
